import { useQuery } from '@apollo/client';
import { AddressAutofill } from '@mapbox/search-js-react';
import {
  CountrySelect,
  FieldContainer,
  Input,
  RcSelect,
  RegionSelect,
} from '@retainerclub/common';
import { GET_ACCOUNT_CREDIT_CARDS } from '@retainerclub/customer-api';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import CheckoutFormSection from './CheckoutFormSection';

const NEW_CARD_OPTION_ID = 'new-card';

function CheckoutFormPayment() {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const { data, loading } = useQuery(GET_ACCOUNT_CREDIT_CARDS);
  const creditCardId = watch('creditCard.id');
  const [useNewCard, setUseNewCard] = useState(false);

  const accountCreditCards = data?.accountCreditCards || [];

  const cardOptions = useMemo(() => {
    const options = accountCreditCards.map((card) => {
      const expiryMonth = String(card.expiryMonth).padStart(2, '0');
      const expiryYear = String(card.expiryYear).slice(-2);
      const defaultLabel = card.defaultCard ? ' (Default)' : '';
      return {
        id: card.id,
        name: `${card.brand} ending in ${card.maskedNumber} • ${card.nameOnCard} • Expires ${expiryMonth}/${expiryYear}${defaultLabel}`,
        card,
      };
    });

    options.push({
      id: NEW_CARD_OPTION_ID,
      name: '+ Add New',
      card: null,
    });

    return options;
  }, [accountCreditCards]);

  const defaultCard = useMemo(() => {
    return (
      accountCreditCards.find((card) => card.defaultCard) ||
      accountCreditCards[0]
    );
  }, [accountCreditCards]);

  const hasCards = accountCreditCards.length > 0;
  const showCardForm =
    !hasCards || useNewCard || creditCardId === NEW_CARD_OPTION_ID;
  const showBillingAddressForm =
    !hasCards || useNewCard || creditCardId === NEW_CARD_OPTION_ID;

  useEffect(() => {
    if (hasCards && defaultCard && !creditCardId && !useNewCard) {
      setValue('creditCard.id', defaultCard.id);
      setUseNewCard(false);
    }
  }, [hasCards, defaultCard, creditCardId, useNewCard, setValue]);

  useEffect(() => {
    const currentId = creditCardId;
    if (currentId === NEW_CARD_OPTION_ID) {
      setUseNewCard(true);
      setValue('creditCard.nameOnCard', '');
      setValue('creditCard.cardNumber', '');
      setValue('creditCard.expirationDate', '');
      setValue('creditCard.cvv', '');
      setValue('billingAddress.id', null);
    } else if (currentId && currentId !== NEW_CARD_OPTION_ID) {
      setUseNewCard(false);
      setValue('creditCard.nameOnCard', '');
      setValue('creditCard.cardNumber', '');
      setValue('creditCard.expirationDate', '');
      setValue('creditCard.cvv', '');

      // Set billing address ID from selected card
      const card = accountCreditCards.find((c) => c.id === currentId);
      if (card?.billingAddress?.id) {
        setValue('billingAddress.id', card.billingAddress.id);
      } else if (card?.billingAddress) {
        // If billingAddress is an object but no ID, try to find or use the object
        // For now, we'll clear the form fields since we need an ID
        setValue('billingAddress.id', null);
      }
    }
  }, [creditCardId, setValue, accountCreditCards]);

  return (
    <>
      <CheckoutFormSection title="Payment" first>
        {hasCards && (
          <div className="sm:col-span-6">
            <FieldContainer
              name="creditCard.id"
              label="Payment Method"
              required={!showCardForm}
            >
              <RcSelect
                name="creditCard.id"
                data={cardOptions}
                disabled={loading}
                clearable={false}
              />
            </FieldContainer>
          </div>
        )}

        {showCardForm && (
          <>
            <Input
              className="sm:col-span-6"
              required
              name="creditCard.nameOnCard"
              label="Cardholder Name"
              autoComplete="cc-name"
            />

            <Controller
              name="creditCard.cardNumber"
              render={({ field: { onChange, onBlur, name, value, ref } }) => (
                <FieldContainer
                  className="sm:col-span-6"
                  required
                  name="creditCard.cardNumber"
                  label="Card Number"
                >
                  <PatternFormat
                    format="#### #### #### ####"
                    name={name}
                    getInputRef={ref}
                    onBlur={onBlur}
                    value={value}
                    onChange={onChange}
                    autoComplete="cc-number"
                    valueIsNumericString
                    className={`block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700 sm:text-sm sm:leading-6 ${
                      errors?.creditCard?.cardNumber
                        ? 'text-red-900 ring-red-500 placeholder:text-red-500 focus:ring-red-500'
                        : 'ring-gray-300 placeholder:text-gray-400 focus:ring-rcprimary-400'
                    }`}
                    inputMode="numeric"
                  />
                </FieldContainer>
              )}
            />

            <Controller
              name="creditCard.expirationDate"
              render={({ field: { onChange, onBlur, name, value, ref } }) => (
                <FieldContainer
                  className="sm:col-span-2"
                  required
                  name="creditCard.expirationDate"
                  label="Exp. Date"
                >
                  <PatternFormat
                    format="##/##"
                    name={name}
                    getInputRef={ref}
                    onBlur={onBlur}
                    value={value}
                    onChange={onChange}
                    valueIsNumericString
                    allowEmptyFormatting
                    inputMode="numeric"
                    mask="_"
                    autoComplete="cc-exp"
                    className={`block w-full rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700 sm:text-sm sm:leading-6 ${
                      errors?.creditCard?.expirationDate
                        ? 'text-red-900 ring-red-500 placeholder:text-red-500 focus:ring-red-500'
                        : 'ring-gray-300 placeholder:text-gray-400 focus:ring-rcprimary-400'
                    }`}
                  />
                </FieldContainer>
              )}
            />

            <Input
              className="sm:col-span-2"
              required
              name="creditCard.cvv"
              label="CVV"
              autoComplete="cc-csc"
              placeholder="123"
              attributes={{ maxLength: 4 }}
              inputMode="numeric"
            />
          </>
        )}

        <div className="sm:col-span-6">
          <img
            alt="Accepted payment types: Visa, Mastercard, American Express, Discover"
            src="https://cdn.retainerclub.com/images/payment-icons/brand_logos.png"
            className="max-h-10"
          />
        </div>
      </CheckoutFormSection>
      {showBillingAddressForm && (
        <CheckoutFormSection title="Billing Address">
          <div className="sm:col-span-6">
            <AddressAutofill
              accessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
              confirmOnBrowserAutofill={{
                minimap: true,
                skipConfirmModal: (feature) =>
                  ['exact', 'high'].includes(
                    feature.properties.match_code.confidence,
                  ),
              }}
              onRetrieve={(address) => {
                if (address?.features?.length > 0) {
                  const {
                    country_code: countryCode,
                    region_code: regionCode,
                    matching_name: matchingName,
                  } = address.features[0].properties;

                  setValue('billingAddress.country', countryCode.toUpperCase());

                  setValue('billingAddress.region', regionCode.toUpperCase());

                  setValue('billingAddress.address', matchingName);
                }
              }}
              onChange={(event) => {
                setValue('billingAddress.address', event);
              }}
              options={{
                country: 'US',
                language: 'en',
                proximity: 'ip',
              }}
            >
              <Input
                name="billingAddress.address"
                label="Address"
                required
                autoComplete=""
              />
            </AddressAutofill>
          </div>

          <div className="sm:col-span-3">
            <Input
              name="billingAddress.address2"
              label="Address Line 2"
              autoComplete="address-line2"
            />
          </div>
          <div className="sm:col-span-3">
            <Input
              name="billingAddress.city"
              label="City"
              required
              autoComplete="address-level2"
            />
          </div>
          <div className="sm:col-span-2">
            <CountrySelect
              name="billingAddress.country"
              required
              autoComplete=""
            />
          </div>
          <div className="sm:col-span-2">
            <RegionSelect
              name="billingAddress.region"
              countryFieldName="billingAddress.country"
              autoComplete=""
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              name="billingAddress.postalCode"
              label="Postal Code"
              required
              autoComplete="postal-code"
            />
          </div>
        </CheckoutFormSection>
      )}
    </>
  );
}

export default CheckoutFormPayment;
